import { useState, useCallback, useEffect, useMemo } from 'react';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import {
  LOCATIONS as ROOFTOPS,
  PAY_TYPES,
  isExecLevel,
  type EmployeeAssignment,
  type PayPlanTemplate,
  type PayType,
} from '@pay-plan/shared';
import { useApi } from '../hooks/use-api.js';
import { useAuth } from '../context/AuthContext.js';
import {
  listAssignments,
  createAssignment,
  updateAssignment,
  deleteAssignment,
} from '../api/assignments.js';
import { countEmployeePaySheets } from '../api/pay-sheets.js';
import { CurrencyInput } from '../components/CurrencyInput.js';
import { formatCurrency } from '../utils/pay-calculations.js';
import { listTemplates } from '../api/templates.js';
import { listUsers } from '../api/users.js';
import { listUsersByLocation } from '../api/graph.js';
import { syncLocationEmployees } from '../api/employees.js';
import { graphLocationRequest } from '../config/msal-config.js';
import { LoadingSpinner } from '../components/LoadingSpinner.js';
import { EmptyState } from '../components/EmptyState.js';
import { EmployeePicker } from '../components/EmployeePicker.js';
import type { GraphEmployee } from '../api/graph.js';

const PAY_TYPE_LABELS: Record<PayType, string> = {
  salaried: 'Salaried',
  commissioned: 'Commissioned',
  hourly: 'Hourly',
  hybrid: 'Hybrid',
};

export function AssignmentPage(): React.JSX.Element {
  const { instance } = useMsal();
  const { user, role } = useAuth();
  // Admin/Executive/Accounting manage assignments company-wide; General Managers
  // are read-only (and don't see this page in the nav, but guard direct URLs).
  const isCompanyWide = isExecLevel(role);
  const canManage = isExecLevel(role);
  const visibleLocations = isCompanyWide
    ? ROOFTOPS
    : user?.companyName !== undefined && user.companyName !== ''
      ? ([user.companyName] as readonly string[])
      : ([] as readonly string[]);
  const [locationId, setLocationId] = useState(visibleLocations[0] ?? '');
  const [showForm, setShowForm] = useState(false);
  const [editTarget, setEditTarget] = useState<EmployeeAssignment | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<EmployeeAssignment | null>(null);
  const [deletePaySheetCount, setDeletePaySheetCount] = useState<number | null>(null);
  const [deleteCascade, setDeleteCascade] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [syncDegraded, setSyncDegraded] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const payTypeFilter = useMemo(() => {
    const raw = searchParams.get('payType');
    if (raw === null || raw === '') return new Set<PayType>();
    return new Set(raw.split(',').filter((p): p is PayType => PAY_TYPES.includes(p as PayType)));
  }, [searchParams]);
  const roleFilter = searchParams.get('role') ?? '';

  function togglePayType(pt: PayType): void {
    const next = new Set(payTypeFilter);
    if (next.has(pt)) next.delete(pt);
    else next.add(pt);
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (next.size === 0) p.delete('payType');
        else p.set('payType', Array.from(next).join(','));
        return p;
      },
      { replace: true },
    );
  }

  function setRoleFilter(value: string): void {
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (value.trim() === '') p.delete('role');
        else p.set('role', value);
        return p;
      },
      { replace: true },
    );
  }

  const {
    data: assignments,
    loading: assignLoading,
    error: assignError,
    refetch,
  } = useApi(async () => listAssignments(locationId), [locationId]);

  const { data: templates } = useApi(listTemplates, []);
  const { data: users } = useApi(listUsers, []);

  const publishedTemplates = templates?.filter((t) => t.status === 'published') ?? [];
  const draftTemplateCount = templates?.filter((t) => t.status === 'draft').length ?? 0;

  const filteredAssignments = useMemo(() => {
    if (assignments === null) return null;
    const templateById = new Map((templates ?? []).map((t) => [t.templateId, t]));
    const needle = roleFilter.trim().toLowerCase();
    return assignments.filter((a) => {
      const tmpl = templateById.get(a.templateId);
      if (payTypeFilter.size > 0) {
        if (tmpl === undefined || !payTypeFilter.has(tmpl.payType)) return false;
      }
      if (needle !== '') {
        const role = (tmpl?.roleLabel ?? '').toLowerCase();
        if (!role.includes(needle)) return false;
      }
      return true;
    });
  }, [assignments, templates, payTypeFilter, roleFilter]);

  // Sync Graph roster for this location to the backend (fire-and-forget).
  // Enables server-side validation that assignments stay within rooftop.
  // Silent-only: never trigger popups — if User.Read.All hasn't been
  // consented yet, skip the sync and let the EmployeePicker handle
  // consent interactively when the user actually opens it. Read-only roles
  // (General Managers) never sync — the roster write requires exec-level access.
  useEffect(() => {
    if (locationId === '' || !canManage) return;
    const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0];
    if (account === undefined) return;
    setSyncDegraded(false);
    void instance
      .acquireTokenSilent({ ...graphLocationRequest, account })
      .then(async () => listUsersByLocation(instance, locationId))
      .then(async (employees) => {
        await syncLocationEmployees(locationId, employees);
      })
      .catch((err: unknown) => {
        // eslint-disable-next-line no-console
        console.error('[AssignmentPage] location sync failed', err);
        setSyncDegraded(true);
      });
  }, [locationId, instance, canManage]);

  function userDisplayName(a: Pick<EmployeeAssignment, 'employeeId' | 'employeeName'>): string {
    if (a.employeeName !== undefined && a.employeeName !== '') return a.employeeName;
    const found = users?.find((u) => u.userId === a.employeeId);
    return found?.displayName ?? a.employeeId;
  }

  function templateName(templateId: string): string {
    const tmpl = templates?.find((t) => t.templateId === templateId);
    return tmpl?.name ?? templateId;
  }

  async function openDeleteDialog(a: EmployeeAssignment): Promise<void> {
    setDeleteTarget(a);
    setDeleteCascade(false);
    setDeletePaySheetCount(null);
    try {
      const count = await countEmployeePaySheets(a.locationId, a.employeeId);
      setDeletePaySheetCount(count);
    } catch {
      setDeletePaySheetCount(0);
    }
  }

  async function handleDelete(): Promise<void> {
    if (deleteTarget === null) return;
    setDeleteBusy(true);
    try {
      await deleteAssignment(
        deleteTarget.locationId,
        deleteTarget.employeeId,
        deleteTarget.assignmentId,
        deleteCascade,
      );
    } finally {
      setDeleteBusy(false);
    }
    setDeleteTarget(null);
    refetch();
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <label className="text-sm font-medium text-gray-700">Location:</label>
          <select
            value={locationId}
            onChange={(e) => setLocationId(e.target.value)}
            disabled={!isCompanyWide}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand focus:ring-1 focus:ring-brand focus:outline-none disabled:bg-gray-100 disabled:text-gray-500"
          >
            {visibleLocations.map((loc) => (
              <option key={loc} value={loc}>
                {loc}
              </option>
            ))}
          </select>
        </div>
        {canManage && (
          <button
            type="button"
            onClick={() => setShowForm(true)}
            className="inline-flex items-center gap-2 rounded-lg bg-brand px-4 py-2 text-sm font-medium text-white hover:bg-brand-dark transition-colors"
          >
            <Plus size={16} />
            Assign Employee
          </button>
        )}
      </div>

      {showForm && (
        <AssignForm
          locationId={locationId}
          templates={publishedTemplates}
          draftTemplateCount={draftTemplateCount}
          onSaved={() => {
            setShowForm(false);
            refetch();
          }}
          onCancel={() => setShowForm(false)}
        />
      )}

      {editTarget !== null && (
        <AssignForm
          locationId={locationId}
          templates={publishedTemplates}
          draftTemplateCount={draftTemplateCount}
          editing={editTarget}
          editingEmployeeName={userDisplayName(editTarget)}
          onSaved={() => {
            setEditTarget(null);
            refetch();
          }}
          onCancel={() => setEditTarget(null)}
        />
      )}

      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4 flex flex-wrap items-center gap-3">
        <span className="text-xs font-medium uppercase tracking-wide text-gray-500 mr-1">
          Filter:
        </span>
        {PAY_TYPES.map((pt) => {
          const active = payTypeFilter.has(pt);
          return (
            <button
              key={pt}
              type="button"
              onClick={() => togglePayType(pt)}
              className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                active
                  ? 'bg-brand text-white border-brand'
                  : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
              }`}
            >
              {PAY_TYPE_LABELS[pt]}
            </button>
          );
        })}
        <input
          type="text"
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          placeholder="Filter by role label..."
          className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs focus:border-brand focus:ring-1 focus:ring-brand focus:outline-none"
        />
        {(payTypeFilter.size > 0 || roleFilter !== '') && (
          <button
            type="button"
            onClick={() => {
              setSearchParams(
                (prev) => {
                  const p = new URLSearchParams(prev);
                  p.delete('payType');
                  p.delete('role');
                  return p;
                },
                { replace: true },
              );
            }}
            className="text-xs text-gray-500 hover:text-gray-700 underline"
          >
            Clear
          </button>
        )}
      </div>

      {syncDegraded && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800 mb-4">
          Roster sync is degraded — employee names, managers, or rooftop changes may be stale.
          Refresh or contact support if this persists.
        </div>
      )}

      {assignLoading ? (
        <LoadingSpinner />
      ) : assignError !== null ? (
        <div className="rounded-lg bg-red-50 p-4 text-sm text-red-700">
          Failed to load assignments: {assignError}
        </div>
      ) : filteredAssignments === null || filteredAssignments.length === 0 ? (
        <EmptyState
          title={
            assignments !== null && assignments.length > 0
              ? 'No assignments match the filters'
              : 'No assignments'
          }
          description={
            assignments !== null && assignments.length > 0
              ? 'Clear filters to see all assignments for this location.'
              : 'Assign a published template to an employee to get started.'
          }
        />
      ) : (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <Th>Employee</Th>
                <Th>Template</Th>
                <Th>Version</Th>
                <Th>Base Override</Th>
                <Th>Salary Override</Th>
                <Th>Assigned</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredAssignments.map((a) => (
                <tr key={a.assignmentId} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">
                    {userDisplayName(a)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{templateName(a.templateId)}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">v{a.templateVersion}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {a.baseCommissionAmountOverride !== undefined
                      ? formatCurrency(a.baseCommissionAmountOverride)
                      : a.baseCommissionRate2Override !== undefined
                        ? `${a.baseCommissionRateOverride !== undefined ? `${a.baseCommissionRateOverride}%` : '—'} / ${a.baseCommissionRate2Override}%`
                        : a.baseCommissionRateOverride !== undefined
                          ? `${a.baseCommissionRateOverride}%`
                          : '—'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {a.weeklySalaryOverride !== undefined ? `$${a.weeklySalaryOverride}` : '—'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {new Date(a.assignedAt).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3">
                    {canManage ? (
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          onClick={() => setEditTarget(a)}
                          className="text-brand hover:text-brand-dark"
                          title="Edit overrides"
                        >
                          <Pencil size={16} />
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            void openDeleteDialog(a);
                          }}
                          className="text-red-500 hover:text-red-700"
                          title="Remove assignment"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400">View only</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {deleteTarget !== null && (
        <DeleteAssignmentDialog
          employeeName={userDisplayName(deleteTarget)}
          paySheetCount={deletePaySheetCount}
          cascade={deleteCascade}
          onCascadeChange={setDeleteCascade}
          busy={deleteBusy}
          onConfirm={() => {
            void handleDelete();
          }}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}

interface DeleteAssignmentDialogProps {
  employeeName: string;
  paySheetCount: number | null;
  cascade: boolean;
  onCascadeChange: (next: boolean) => void;
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

function DeleteAssignmentDialog({
  employeeName,
  paySheetCount,
  cascade,
  onCascadeChange,
  busy,
  onConfirm,
  onCancel,
}: DeleteAssignmentDialogProps): React.JSX.Element {
  const hasSheets = paySheetCount !== null && paySheetCount > 0;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={busy ? undefined : onCancel} />
      <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
        <h3 className="text-lg font-semibold text-gray-900">Remove Assignment</h3>
        <p className="mt-2 text-sm text-gray-600">
          Remove the template assignment for <span className="font-medium">{employeeName}</span>?
        </p>

        {paySheetCount === null ? (
          <p className="mt-4 text-xs text-gray-500">Checking for completed pay sheets…</p>
        ) : hasSheets ? (
          <label className="mt-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm">
            <input
              type="checkbox"
              checked={cascade}
              onChange={(e) => onCascadeChange(e.target.checked)}
              className="mt-0.5"
            />
            <span className="text-red-800">
              Also delete this employee&apos;s {paySheetCount} completed pay sheet
              {paySheetCount === 1 ? '' : 's'} at this location.{' '}
              <span className="font-medium">This cannot be undone.</span>
            </span>
          </label>
        ) : (
          <p className="mt-4 text-xs text-gray-500">
            No completed pay sheets exist for this employee at this location.
          </p>
        )}

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy || paySheetCount === null}
            className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
          >
            {busy ? 'Removing…' : 'Remove'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }): React.JSX.Element {
  return (
    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
      {children}
    </th>
  );
}

interface AssignFormProps {
  locationId: string;
  templates: PayPlanTemplate[];
  draftTemplateCount: number;
  editing?: EmployeeAssignment;
  editingEmployeeName?: string;
  onSaved: () => void;
  onCancel: () => void;
}

function AssignForm({
  locationId,
  templates,
  draftTemplateCount,
  editing,
  editingEmployeeName,
  onSaved,
  onCancel,
}: AssignFormProps): React.JSX.Element {
  const isEdit = editing !== undefined;
  const [selectedEmployee, setSelectedEmployee] = useState<GraphEmployee | null>(null);
  const [templateId, setTemplateId] = useState(editing?.templateId ?? '');
  const [rateOverride, setRateOverride] = useState(
    editing?.baseCommissionRateOverride !== undefined
      ? String(editing.baseCommissionRateOverride)
      : '',
  );
  const [rate2Override, setRate2Override] = useState(
    editing?.baseCommissionRate2Override !== undefined
      ? String(editing.baseCommissionRate2Override)
      : '',
  );
  const [amountOverride, setAmountOverride] = useState(editing?.baseCommissionAmountOverride ?? 0);
  const [salaryOverride, setSalaryOverride] = useState(
    editing?.weeklySalaryOverride !== undefined ? String(editing.weeklySalaryOverride) : '',
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedTemplate = templates.find((t) => t.templateId === templateId);
  const baseType = selectedTemplate?.baseCommissionType ?? 'percent';
  // Dual-rate templates get a second override box, one per gross rate.
  const templateDualRate =
    baseType === 'percent' &&
    selectedTemplate?.baseCommissionRate2 !== undefined &&
    selectedTemplate.grossLabel2.trim() !== '';

  const handleSubmit = useCallback(async () => {
    if (isEdit && editing !== undefined) {
      setSaving(true);
      setError(null);
      try {
        await updateAssignment(locationId, editing.assignmentId, {
          baseCommissionRateOverride:
            baseType === 'percent' && rateOverride !== '' ? parseFloat(rateOverride) : undefined,
          baseCommissionRate2Override:
            templateDualRate && rate2Override !== '' ? parseFloat(rate2Override) : undefined,
          baseCommissionAmountOverride:
            baseType === 'flat' && amountOverride > 0 ? amountOverride : undefined,
          weeklySalaryOverride: salaryOverride !== '' ? parseFloat(salaryOverride) : undefined,
        });
        onSaved();
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Failed to update assignment');
      } finally {
        setSaving(false);
      }
      return;
    }

    if (
      selectedEmployee === null ||
      selectedEmployee.id === '' ||
      templateId === '' ||
      selectedTemplate === undefined
    )
      return;
    setSaving(true);
    setError(null);

    try {
      await createAssignment(locationId, {
        employeeId: selectedEmployee.id,
        employeeName: selectedEmployee.displayName,
        templateId,
        templateVersion: selectedTemplate.version,
        baseCommissionRateOverride:
          baseType === 'percent' && rateOverride !== '' ? parseFloat(rateOverride) : undefined,
        baseCommissionRate2Override:
          templateDualRate && rate2Override !== '' ? parseFloat(rate2Override) : undefined,
        baseCommissionAmountOverride:
          baseType === 'flat' && amountOverride > 0 ? amountOverride : undefined,
        weeklySalaryOverride: salaryOverride !== '' ? parseFloat(salaryOverride) : undefined,
      });
      onSaved();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to create assignment');
    } finally {
      setSaving(false);
    }
  }, [
    isEdit,
    editing,
    selectedEmployee?.id ?? null,
    selectedEmployee?.displayName ?? null,
    templateId,
    selectedTemplate,
    baseType,
    templateDualRate,
    locationId,
    rateOverride,
    rate2Override,
    amountOverride,
    salaryOverride,
    onSaved,
  ]);

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
      <h3 className="text-sm font-semibold text-gray-900 mb-4">
        {isEdit ? 'Edit Assignment' : 'New Assignment'}
      </h3>
      {error !== null && (
        <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700 mb-4">{error}</div>
      )}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Employee</label>
          {isEdit ? (
            <div className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
              {editingEmployeeName ?? editing?.employeeId ?? ''}
            </div>
          ) : (
            <EmployeePicker
              value={selectedEmployee}
              onSelect={setSelectedEmployee}
              disabled={saving}
              location={locationId}
            />
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Template</label>
          {isEdit ? (
            <div className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
              {selectedTemplate?.name ?? templateId} (v{editing?.templateVersion ?? ''})
            </div>
          ) : (
            <>
              <select
                value={templateId}
                onChange={(e) => setTemplateId(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand focus:ring-1 focus:ring-brand focus:outline-none"
              >
                <option value="">Select template...</option>
                {templates.map((t) => (
                  <option key={t.templateId} value={t.templateId}>
                    {t.name} (v{t.version})
                  </option>
                ))}
              </select>
              {templates.length === 0 && draftTemplateCount > 0 && (
                <p className="mt-1 text-xs text-amber-700">
                  Only published templates appear here. You have {draftTemplateCount} draft
                  {draftTemplateCount === 1 ? '' : 's'} — publish from the Templates page to make
                  them assignable.
                </p>
              )}
            </>
          )}
        </div>
        {baseType === 'percent' ? (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {templateDualRate ? 'Base Rate Override — Gross 1 (%)' : 'Base Rate Override (%)'}
            </label>
            <input
              type="number"
              min={0}
              max={100}
              step={0.1}
              value={rateOverride}
              onChange={(e) => setRateOverride(e.target.value)}
              placeholder={
                selectedTemplate !== undefined
                  ? `Default: ${selectedTemplate.baseCommissionRate ?? 0}%`
                  : 'Optional'
              }
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand focus:ring-1 focus:ring-brand focus:outline-none"
            />
          </div>
        ) : (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Base Amount Override ($)
            </label>
            <CurrencyInput
              value={amountOverride}
              onChange={setAmountOverride}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand focus:ring-1 focus:ring-brand focus:outline-none"
              placeholder={
                selectedTemplate !== undefined
                  ? `Default: ${formatCurrency(selectedTemplate.baseCommissionAmount ?? 0)}`
                  : 'Optional'
              }
            />
          </div>
        )}
        {templateDualRate && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Base Rate Override — Gross 2 (%)
            </label>
            <input
              type="number"
              min={0}
              max={100}
              step={0.1}
              value={rate2Override}
              onChange={(e) => setRate2Override(e.target.value)}
              placeholder={`Default: ${selectedTemplate?.baseCommissionRate2 ?? 0}%`}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand focus:ring-1 focus:ring-brand focus:outline-none"
            />
          </div>
        )}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Weekly Salary Override ($)
          </label>
          <input
            type="number"
            min={0}
            step={1}
            value={salaryOverride}
            onChange={(e) => setSalaryOverride(e.target.value)}
            placeholder={
              selectedTemplate !== undefined
                ? `Default: $${selectedTemplate.weeklySalary}`
                : 'Optional'
            }
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand focus:ring-1 focus:ring-brand focus:outline-none"
          />
        </div>
      </div>
      <div className="flex justify-end gap-3 mt-4">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={
            saving ||
            (!isEdit &&
              (selectedEmployee === null || selectedEmployee.id === '' || templateId === ''))
          }
          onClick={() => {
            void handleSubmit();
          }}
          className="rounded-lg bg-brand px-4 py-2 text-sm font-medium text-white hover:bg-brand-dark disabled:opacity-50"
        >
          {saving ? (isEdit ? 'Saving...' : 'Assigning...') : isEdit ? 'Save Changes' : 'Assign'}
        </button>
      </div>
    </div>
  );
}
